import React, { useMemo, useState } from 'react';
import './BybTelemetryPanel.css';

const SAMPLE_LIMIT = 3500;
const HIST_BINS = 42;

const METRICS = [
  { key: 'fork', label: 'Front suspension (%)', color: '#42a5f5' },
  { key: 'shock', label: 'Rear suspension (%)', color: '#ef4444' },
  { key: 'speed', label: 'Speed (km/h)', color: '#7dd3fc' },
  { key: 'front_brake', label: 'Front brake', color: '#eab308' },
  { key: 'rear_brake', label: 'Rear brake', color: '#ef4444' },
  { key: 'accel_x', label: 'Acc X (g)', color: '#c084fc' },
  { key: 'accel_y', label: 'Acc Y (g)', color: '#60a5fa' },
  { key: 'accel_z', label: 'Acc Z (g)', color: '#f59e0b' },
  { key: 'gyro_x', label: 'Gyro X', color: '#a78bfa' },
  { key: 'gyro_y', label: 'Gyro Y', color: '#34d399' },
  { key: 'gyro_z', label: 'Gyro Z', color: '#fb7185' },
  { key: 'gps_speed', label: 'GPS speed', color: '#22d3ee' },
];

const metricByKey = new Map(METRICS.map((m) => [m.key, m]));

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function rowTime(row, index) {
  return num(row?.time ?? row?.Time ?? row?.timestamp) ?? index;
}

function value(row, key) {
  if (!row) return null;
  if (key === 'fork') return num(row.fork_normalized ?? row.fork);
  if (key === 'shock') return num(row.shock_normalized ?? row.shock);
  if (key === 'gps_speed') return num(row.gps_speed ?? row.speed);
  return num(row[key]);
}

function downsample(rows, limit = SAMPLE_LIMIT) {
  if (!Array.isArray(rows) || rows.length <= limit) return rows || [];
  const step = Math.ceil(rows.length / limit);
  const out = [];
  for (let i = 0; i < rows.length; i += step) out.push(rows[i]);
  return out;
}

function range(values, fallback = { min: 0, max: 1 }) {
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min === Infinity) return fallback;
  if (min === max) return { min: min - 1, max: max + 1 };
  return { min, max };
}

function stats(rows, key) {
  const vals = rows.map((row) => value(row, key)).filter(Number.isFinite);
  if (!vals.length) return { avg: 0, max: 0, min: 0 };
  return {
    avg: vals.reduce((a, b) => a + b, 0) / vals.length,
    max: Math.max(...vals),
    min: Math.min(...vals),
  };
}

function pathFor(rows, key, xRange, yRange) {
  const pts = rows
    .map((row, index) => ({ x: rowTime(row, index), y: value(row, key) }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  if (!pts.length) return '';
  return pts
    .map((p, i) => {
      const x = ((p.x - xRange.min) / (xRange.max - xRange.min)) * 100;
      const y = 100 - ((p.y - yRange.min) / (yRange.max - yRange.min)) * 100;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(3)} ${y.toFixed(3)}`;
    })
    .join(' ');
}

function makeBins(values, count = HIST_BINS) {
  const finite = values.filter(Number.isFinite);
  const r = range(finite, null);
  if (!r) return { bins: [], range: null };
  const out = new Array(count).fill(0);
  for (const v of finite) {
    const idx = Math.max(0, Math.min(count - 1, Math.floor(((v - r.min) / (r.max - r.min)) * count)));
    out[idx] += 1;
  }
  return { bins: out, range: r };
}

function SidebarGroup({ title, children }) {
  return (
    <section className="byb-side-group">
      <div className="byb-side-title">{title}</div>
      {children}
    </section>
  );
}

function RadioRow({ active, children, onClick }) {
  return (
    <button type="button" className={`byb-radio-row${active ? ' is-active' : ''}`} onClick={onClick}>
      <span className="byb-radio-dot" />
      <span>{children}</span>
    </button>
  );
}

function ChartFrame({ title, children, yLabel, xLabel, rightLabel }) {
  return (
    <div className="byb-chart-frame">
      <div className="byb-chart-topline">
        <span>{title}</span>
      </div>
      <div className="byb-chart-body">
        {yLabel ? <div className="byb-axis-label byb-axis-label--y">{yLabel}</div> : null}
        {rightLabel ? <div className="byb-axis-label byb-axis-label--right">{rightLabel}</div> : null}
        {children}
        {xLabel ? <div className="byb-axis-label byb-axis-label--x">{xLabel}</div> : null}
      </div>
    </div>
  );
}

function LineChart({ rows, metrics, yDomain, title = '', xLabel = 'Time (s)', yLabel = '' }) {
  const xRange = range(rows.map(rowTime));
  const yRange = yDomain || range(metrics.flatMap((key) => rows.map((row) => value(row, key))));
  return (
    <ChartFrame title={title} xLabel={xLabel} yLabel={yLabel}>
      <svg className="byb-chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
        <Grid />
        {metrics.map((key) => (
          <path key={key} d={pathFor(rows, key, xRange, yRange)} fill="none" stroke={metricByKey.get(key)?.color || '#18c37e'} strokeWidth="0.22" vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
    </ChartFrame>
  );
}

function Grid() {
  return (
    <>
      {[0, 25, 50, 75, 100].map((x) => <line key={`v-${x}`} x1={x} y1="0" x2={x} y2="100" className="byb-grid-line" />)}
      {[0, 25, 50, 75, 100].map((y) => <line key={`h-${y}`} x1="0" y1={y} x2="100" y2={y} className="byb-grid-line" />)}
      <line x1="0" y1="100" x2="100" y2="100" className="byb-axis-line" />
      <line x1="0" y1="0" x2="0" y2="100" className="byb-axis-line byb-axis-line--red" />
    </>
  );
}

function HistogramChart({ rows, metrics, title, xLabel = '', yLabel = 'Percentage (%)' }) {
  const histograms = metrics.map((key) => ({ key, ...makeBins(rows.map((row) => value(row, key))) }));
  const maxCount = Math.max(1, ...histograms.flatMap((h) => h.bins));
  const barWidth = 100 / HIST_BINS;
  return (
    <ChartFrame title={title} xLabel={xLabel} yLabel={yLabel}>
      <svg className="byb-chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
        <Grid />
        {histograms.map((hist, histIndex) => hist.bins.map((count, i) => {
          const h = (count / maxCount) * 96;
          const x = i * barWidth + histIndex * (barWidth / Math.max(1, metrics.length));
          return <rect key={`${hist.key}-${i}`} x={x} y={100 - h} width={Math.max(0.35, barWidth / metrics.length)} height={h} fill={metricByKey.get(hist.key)?.color || '#18c37e'} opacity={histIndex === 0 ? 0.68 : 0.56} />;
        }))}
      </svg>
    </ChartFrame>
  );
}

function ScatterChart({ rows, xKey, yKey, title, xLabel, yLabel }) {
  const points = rows.map((row) => ({ x: value(row, xKey), y: value(row, yKey) })).filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  const xRange = range(points.map((p) => p.x));
  const yRange = range(points.map((p) => p.y));
  return (
    <ChartFrame title={title} xLabel={xLabel} yLabel={yLabel}>
      <svg className="byb-chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
        <Grid />
        {points.map((p, i) => {
          const x = ((p.x - xRange.min) / (xRange.max - xRange.min)) * 100;
          const y = 100 - ((p.y - yRange.min) / (yRange.max - yRange.min)) * 100;
          return <circle key={i} cx={x} cy={y} r="0.11" fill="#c8f3ff" opacity="0.32" />;
        })}
      </svg>
    </ChartFrame>
  );
}

function StatRail({ rows }) {
  const items = [
    ['Fork (%)', 'fork'],
    ['Shock (%)', 'shock'],
    ['Speed', 'speed'],
    ['IMU (g)', 'accel_z'],
    ['Front brake', 'front_brake'],
    ['Rear brake', 'rear_brake'],
  ];
  return (
    <div className="byb-stack-stats">
      {items.map(([label, key]) => {
        const s = stats(rows, key);
        return (
          <div key={key} className="byb-stack-stat">
            <div>{label}</div>
            <span>Avg {s.avg.toFixed(1)}</span>
            <span>Max {s.max.toFixed(1)}</span>
          </div>
        );
      })}
    </div>
  );
}

function StackView({ rows }) {
  return (
    <div className="byb-stack-layout">
      <div className="byb-stack-bottom">
        <StatRail rows={rows} />
        <div className="byb-stack-traces">
          <LineChart rows={rows} metrics={['fork']} yDomain={{ min: 0, max: 100 }} title="Front suspension (%)" yLabel="Travel (%)" />
          <LineChart rows={rows} metrics={['shock']} yDomain={{ min: 0, max: 100 }} title="Rear suspension (%)" yLabel="Travel (%)" />
          <LineChart rows={rows} metrics={['speed']} title="Speed (km/h)" />
          <LineChart rows={rows} metrics={['accel_z']} title="IMU (g)" />
          <LineChart rows={rows} metrics={['front_brake', 'rear_brake']} title="Brakes" />
        </div>
      </div>
    </div>
  );
}

function PanelShell({ sidebar, children }) {
  return (
    <div className="byb-telemetry-panel byb-telemetry-panel--with-sidebar">
      <aside className="byb-control-rail">{sidebar}</aside>
      <main className="byb-chart-grid">{children}</main>
    </div>
  );
}

export default function BybTelemetryPanel({ telemetryData = [], mode = 'stack' }) {
  const rows = useMemo(() => downsample(telemetryData), [telemetryData]);
  const [selectedSusp, setSelectedSusp] = useState('fork');
  const [xKey, setXKey] = useState('fork');
  const [yKey, setYKey] = useState('shock');

  if (!Array.isArray(telemetryData) || telemetryData.length === 0) {
    return <div className="byb-telemetry-empty">No telemetry data for this panel.</div>;
  }

  if (mode === 'stack') return <StackView rows={rows} />;

  if (mode === 'brakes-speed') {
    return (
      <PanelShell
        sidebar={(
          <>
            <SidebarGroup title="Brakes"><RadioRow active>Front</RadioRow><RadioRow active>Rear</RadioRow></SidebarGroup>
            <SidebarGroup title="Speed"><RadioRow active>Sensor</RadioRow><RadioRow>GPS</RadioRow></SidebarGroup>
            <SidebarGroup title="Chart type"><RadioRow active>Area</RadioRow><RadioRow>Lines</RadioRow></SidebarGroup>
          </>
        )}
      >
        <HistogramChart rows={rows} metrics={['front_brake']} title="Front brake pressure distribution" xLabel="Front brake pressure" />
        <HistogramChart rows={rows} metrics={['rear_brake']} title="Rear brake pressure distribution" xLabel="Rear brake pressure" />
        <HistogramChart rows={rows} metrics={['speed']} title="Speed distribution" xLabel="Speed (km/h)" />
      </PanelShell>
    );
  }

  if (mode === 'imu-distrib') {
    return (
      <PanelShell
        sidebar={(
          <>
            <SidebarGroup title="Accelerometer"><RadioRow active>X</RadioRow><RadioRow active>Y</RadioRow><RadioRow active>Z</RadioRow></SidebarGroup>
            <SidebarGroup title="Gyroscope"><RadioRow active>X</RadioRow><RadioRow active>Y</RadioRow><RadioRow active>Z</RadioRow></SidebarGroup>
            <SidebarGroup title="Chart type"><RadioRow active>Area</RadioRow><RadioRow>Lines</RadioRow></SidebarGroup>
          </>
        )}
      >
        <HistogramChart rows={rows} metrics={['accel_x', 'accel_y', 'accel_z']} title="Accelerometer distribution" xLabel="Acceleration (g)" />
        <HistogramChart rows={rows} metrics={['gyro_x', 'gyro_y', 'gyro_z']} title="Gyroscope distribution" xLabel="Gyro" />
      </PanelShell>
    );
  }

  if (mode === 'imu-charts') {
    return (
      <PanelShell
        sidebar={(
          <>
            <SidebarGroup title="Accelerometer"><RadioRow active>X</RadioRow><RadioRow active>Y</RadioRow><RadioRow active>Z</RadioRow></SidebarGroup>
            <SidebarGroup title="Gyroscope"><RadioRow active>X</RadioRow><RadioRow active>Y</RadioRow><RadioRow active>Z</RadioRow></SidebarGroup>
            <SidebarGroup title="Chart type"><RadioRow active>Lines</RadioRow><RadioRow>Scatter</RadioRow></SidebarGroup>
          </>
        )}
      >
        <LineChart rows={rows} metrics={['accel_x', 'accel_y', 'accel_z']} title="Accelerometer charts" xLabel="Time (s)" yLabel="Acceleration (g)" />
        <LineChart rows={rows} metrics={['gyro_x', 'gyro_y', 'gyro_z']} title="Gyroscope charts" xLabel="Time (s)" yLabel="Gyro" />
      </PanelShell>
    );
  }

  if (mode === 'custom-xy') {
    return (
      <PanelShell
        sidebar={(
          <>
            <SidebarGroup title="Axes setup">
              <label className="byb-select-label">X Axis<select value={xKey} onChange={(e) => setXKey(e.target.value)}>{METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}</select></label>
              <label className="byb-select-label">Y Axis<select value={yKey} onChange={(e) => setYKey(e.target.value)}>{METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}</select></label>
            </SidebarGroup>
            <SidebarGroup title="Chart type"><RadioRow active>Lines</RadioRow><RadioRow>Scatter</RadioRow></SidebarGroup>
          </>
        )}
      >
        <ScatterChart rows={rows} xKey={xKey} yKey={yKey} title="Custom XY chart" xLabel={metricByKey.get(xKey)?.label} yLabel={metricByKey.get(yKey)?.label} />
      </PanelShell>
    );
  }

  return (
    <PanelShell
      sidebar={(
        <>
          <SidebarGroup title="Suspension">
            <RadioRow active={selectedSusp === 'fork'} onClick={() => setSelectedSusp('fork')}>Fork</RadioRow>
            <RadioRow active={selectedSusp === 'shock'} onClick={() => setSelectedSusp('shock')}>Shock</RadioRow>
          </SidebarGroup>
          <SidebarGroup title="Chart type"><RadioRow active>Lines</RadioRow><RadioRow>Scatter</RadioRow></SidebarGroup>
        </>
      )}
    >
      <LineChart rows={rows} metrics={[selectedSusp]} yDomain={{ min: 0, max: 100 }} title={selectedSusp === 'fork' ? 'Fork suspension' : 'Shock suspension'} yLabel="Suspension travel (%)" />
    </PanelShell>
  );
}
